/* SOLANA — Parcours guidé de demande de devis
 * Une question par étape, retour possible, réponses conservées
 * (mémoire + sessionStorage), récapitulatif modifiable, envoi via le
 * formulaire de contact natif Shopify. La confirmation ne s'affiche
 * qu'après le retour de Shopify (form.posted_successfully?).
 */
(() => {
  if (window.SolanaDevisLoaded) return;
  window.SolanaDevisLoaded = true;

  const STORE_KEY = 'sp-devis-v1';
  const SENT_KEY = 'sp-devis-sent';
  const GROUP_OCCASIONS = ['clubs', 'bde', 'amis', 'evenement', 'entreprise'];
  const OCCASIONS = ['clubs', 'bde', 'amis', 'evenement', 'entreprise', 'particulier'];
  const ORDER = [
    'occasion', 'projet', 'supports', 'quantites', 'groupe', 'couleurs', 'visuel',
    'perso', 'references', 'date', 'livraison', 'budget', 'contact', 'recap'
  ];

  const store = {
    get(key) { try { return window.sessionStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { window.sessionStorage.setItem(key, value); } catch (e) { /* stockage indisponible */ } },
    remove(key) { try { window.sessionStorage.removeItem(key); } catch (e) { /* stockage indisponible */ } }
  };

  const slug = (text) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const todayIso = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    try {
      return new Date(y, m - 1, d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return iso;
    }
  };

  const isUrlLine = (line) => /^(https?:\/\/|www\.)[^\s]+\.[^\s]{2,}/i.test(line);

  class Devis {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('form');
      this.dialog = root.closest('dialog');
      this.body = root.querySelector('[data-sp-body]');
      this.errorEl = root.querySelector('[data-sp-error]');
      this.countEl = root.querySelector('[data-sp-count]');
      this.barEl = root.querySelector('[data-sp-bar]');
      this.progressEl = root.querySelector('[data-sp-progress]');
      this.footEl = root.querySelector('[data-sp-foot]');
      this.prevBtn = root.querySelector('[data-sp-prev]');
      this.nextBtn = root.querySelector('[data-sp-next]');
      this.submitBtn = root.querySelector('[data-sp-submit]');
      this.recapEl = root.querySelector('[data-sp-recap]');
      this.qtyList = root.querySelector('[data-sp-qty-list]');
      this.steps = {};
      root.querySelectorAll('[data-step]').forEach((el) => { this.steps[el.dataset.step] = el; });
      this.data = {};
      this.current = 'occasion';
      this.returnToRecap = false;
      this.opener = null;

      this.form.noValidate = true;
      const dateInput = this.field('sp_date');
      if (dateInput) dateInput.min = todayIso();

      this.restore();
      this.bind();
      this.handleServerState();
      if (!this.dialog) this.goto(this.current, { focus: false });
    }

    /* ---------- Accès aux champs ---------- */
    field(name) {
      return this.form.querySelector(`[name="${CSS.escape(name)}"]`);
    }

    fields(name) {
      return Array.from(this.form.querySelectorAll(`[name="${CSS.escape(name)}"]`));
    }

    val(name) {
      const els = this.fields(name);
      if (!els.length) return '';
      const type = els[0].type;
      if (type === 'radio') {
        const checked = els.find((el) => el.checked);
        return checked ? checked.value : '';
      }
      if (type === 'checkbox') return els.filter((el) => el.checked).map((el) => el.value);
      return (els[0].value || '').trim();
    }

    labelOf(name) {
      const checked = this.fields(name).find((el) => el.checked);
      if (!checked) return '';
      const span = checked.parentElement.querySelector('span');
      if (!span) return checked.value;
      const clone = span.cloneNode(true);
      clone.querySelectorAll('small').forEach((s) => s.remove());
      return clone.textContent.trim();
    }

    supports() {
      return this.val('sp_supports');
    }

    concreteSupports() {
      return this.supports().filter((s) => s !== 'À conseiller');
    }

    qtyRows() {
      const concrete = this.concreteSupports();
      if (concrete.length) return concrete;
      if (this.supports().includes('À conseiller')) return ['Quantité totale'];
      return [];
    }

    totalQty() {
      return this.qtyRows().reduce((sum, s) => sum + (parseInt(this.data[`sp_qty_${slug(s)}`], 10) || 0), 0);
    }

    isGroup() {
      return GROUP_OCCASIONS.includes(this.val('sp_occasion')) || this.totalQty() >= 2;
    }

    visibleSteps() {
      return ORDER.filter((key) => {
        if (!this.steps[key]) return false;
        if (key === 'groupe') return this.isGroup();
        return true;
      });
    }

    /* ---------- Sauvegarde ---------- */
    collect() {
      const data = {};
      this.form.querySelectorAll('[data-k]').forEach((el) => {
        if (el.type === 'radio') {
          if (el.checked) data[el.name] = el.value;
        } else if (el.type === 'checkbox') {
          if (!data[el.name]) data[el.name] = [];
          if (el.checked) data[el.name].push(el.value);
        } else {
          data[el.name] = el.value;
        }
      });
      Object.keys(this.data).forEach((key) => {
        if (key.startsWith('sp_qty_') && !(key in data)) data[key] = this.data[key];
      });
      this.data = data;
      if (this.current === 'done') return;
      store.set(STORE_KEY, JSON.stringify({ data, step: this.current }));
    }

    apply(data) {
      this.form.querySelectorAll('[data-k]').forEach((el) => {
        const value = data[el.name];
        if (value === undefined) return;
        if (el.type === 'radio') el.checked = el.value === value;
        else if (el.type === 'checkbox') el.checked = Array.isArray(value) && value.includes(el.value);
        else el.value = value;
      });
    }

    restore() {
      const raw = store.get(STORE_KEY);
      if (!raw) return;
      try {
        const saved = JSON.parse(raw);
        this.data = saved.data || {};
        this.apply(this.data);
        if (saved.step && ORDER.includes(saved.step)) this.current = saved.step;
      } catch (e) {
        store.remove(STORE_KEY);
      }
    }

    reset() {
      this.form.reset();
      this.data = {};
      this.current = 'occasion';
      this.returnToRecap = false;
      store.remove(STORE_KEY);
      this.submitBtn.disabled = false;
      this.submitBtn.textContent = 'Envoyer ma demande';
      const dateInput = this.field('sp_date');
      if (dateInput) dateInput.min = todayIso();
    }

    /* ---------- Événements ---------- */
    bind() {
      this.form.addEventListener('input', () => this.collect());
      this.form.addEventListener('change', (event) => {
        const t = event.target;
        if (t.name === 'sp_date_flexible' && t.checked) this.field('sp_date').value = '';
        if (t.name === 'sp_date' && t.value) {
          const flex = this.field('sp_date_flexible');
          if (flex) flex.checked = false;
        }
        if (t.name === 'sp_couleurs_tshirt' && t.value === 'Pas encore décidé' && t.checked) {
          this.fields('sp_couleurs_tshirt').forEach((el) => { if (el !== t) el.checked = false; });
        } else if (t.name === 'sp_couleurs_tshirt' && t.checked) {
          this.fields('sp_couleurs_tshirt').forEach((el) => { if (el.value === 'Pas encore décidé') el.checked = false; });
        }
        if (t.name === 'sp_pays') this.updatePostcode();
        if (t.getAttribute('aria-invalid') === 'true') t.removeAttribute('aria-invalid');
        this.collect();
      });

      this.nextBtn.addEventListener('click', () => this.next());
      this.prevBtn.addEventListener('click', () => this.prev());

      this.form.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.isComposing) return;
        const t = event.target;
        if (t.tagName === 'TEXTAREA' || t.tagName === 'BUTTON' || t.tagName === 'A') return;
        if (this.current === 'recap') return;
        event.preventDefault();
        this.next();
      });

      this.form.addEventListener('submit', (event) => this.onSubmit(event));

      this.recapEl.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-goto]');
        if (!btn) return;
        this.returnToRecap = true;
        this.goto(btn.dataset.goto);
      });

      this.root.querySelectorAll('[data-sp-devis-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });

      if (this.dialog) {
        this.dialog.addEventListener('close', () => this.afterClose());
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });
      }

      this.updatePostcode();
    }

    updatePostcode() {
      const cp = this.field('sp_cp');
      if (!cp) return;
      const isFrance = this.val('sp_pays') === 'France';
      cp.inputMode = isFrance ? 'numeric' : 'text';
      cp.maxLength = isFrance ? 5 : 12;
    }

    /* ---------- Ouverture / fermeture ---------- */
    open(occasion) {
      if (this.current === 'done') this.reset();
      if (occasion && OCCASIONS.includes(occasion)) {
        const radio = this.fields('sp_occasion').find((el) => el.value === occasion);
        if (radio) radio.checked = true;
        this.collect();
        if (this.current !== 'occasion' && this.current !== 'recap') this.current = 'occasion';
      }
      this.opener = document.activeElement;
      if (this.dialog) {
        if (!this.dialog.open) {
          if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
          else this.dialog.setAttribute('open', '');
        }
        document.documentElement.style.overflow = 'hidden';
      } else {
        this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      this.goto(this.current);
    }

    close() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    afterClose() {
      document.documentElement.style.overflow = '';
      if (/^#devis/.test(window.location.hash)) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      if (this.current === 'done') this.reset();
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
    }

    /* ---------- Navigation ---------- */
    goto(key, options = {}) {
      const focus = options.focus !== false;
      if (!this.steps[key]) key = 'occasion';
      if (key === 'quantites') this.renderQty();
      if (key === 'couleurs') this.renderColors();
      if (key === 'recap') this.renderRecap();

      Object.values(this.steps).forEach((el) => { el.hidden = true; });
      this.steps[key].hidden = false;
      this.current = key;
      this.hideError();
      this.updateChrome();
      this.collect();

      if (this.body) this.body.scrollTop = 0;
      if (focus) {
        const title = this.steps[key].querySelector('.sp-q__title');
        if (title) window.requestAnimationFrame(() => title.focus({ preventScroll: true }));
      }
    }

    updateChrome() {
      const isDone = this.current === 'done';
      this.progressEl.hidden = isDone;
      this.footEl.hidden = isDone;
      if (isDone) return;

      const visible = this.visibleSteps();
      const index = visible.indexOf(this.current);
      const total = visible.length;
      if (this.current === 'recap') {
        this.countEl.textContent = `Récapitulatif — étape ${total} sur ${total}`;
      } else {
        this.countEl.textContent = `Étape ${index + 1} sur ${total}`;
      }
      this.barEl.style.width = `${Math.round(((index + 1) / total) * 100)}%`;

      this.prevBtn.hidden = index <= 0;
      const atRecap = this.current === 'recap';
      this.nextBtn.hidden = atRecap;
      this.submitBtn.hidden = !atRecap;
      if (this.returnToRecap && !atRecap) this.nextBtn.textContent = 'Revenir au récapitulatif';
      else if (this.current === 'contact') this.nextBtn.textContent = 'Voir le récapitulatif';
      else this.nextBtn.textContent = 'Suivant';
    }

    next() {
      if (!this.validate(this.current)) return;
      if (this.returnToRecap) {
        const invalid = this.firstInvalid();
        if (invalid && invalid !== this.current) {
          this.goto(invalid);
          this.validate(invalid);
          return;
        }
        this.returnToRecap = false;
        this.goto('recap');
        return;
      }
      const visible = this.visibleSteps();
      const index = visible.indexOf(this.current);
      if (index < visible.length - 1) this.goto(visible[index + 1]);
    }

    prev() {
      const visible = this.visibleSteps();
      const index = visible.indexOf(this.current);
      if (index > 0) this.goto(visible[index - 1]);
    }

    firstInvalid() {
      return this.visibleSteps().find((key) => key !== 'recap' && this.check(key).length > 0) || null;
    }

    /* ---------- Rendu dynamique ---------- */
    renderQty() {
      const rows = this.qtyRows();
      this.qtyList.innerHTML = '';
      rows.forEach((name) => {
        const key = `sp_qty_${slug(name)}`;
        const id = `${this.form.id}-${key}`;
        const row = document.createElement('div');
        row.className = 'sp-qty__row';
        const label = document.createElement('label');
        label.htmlFor = id;
        label.textContent = name === 'Quantité totale' ? 'Quantité totale estimée' : name;
        const input = document.createElement('input');
        input.className = 'sp-input';
        input.type = 'number';
        input.inputMode = 'numeric';
        input.min = '1';
        input.max = '10000';
        input.step = '1';
        input.id = id;
        input.name = key;
        input.setAttribute('data-k', '');
        input.value = this.data[key] || '';
        row.append(label, input);
        this.qtyList.append(row);
      });
    }

    renderColors() {
      const supports = this.supports();
      const hasTshirt = supports.includes('T-shirt');
      const others = supports.filter((s) => s !== 'T-shirt');
      this.root.querySelector('[data-sp-tshirt-colors]').hidden = !hasTshirt;
      const otherBlock = this.root.querySelector('[data-sp-other-colors]');
      otherBlock.hidden = hasTshirt && others.length === 0;
      const label = this.root.querySelector('[data-sp-other-colors-label]');
      if (label) {
        const text = hasTshirt ? 'Couleurs souhaitées pour les autres supports ' : 'Couleurs souhaitées ';
        label.firstChild.textContent = text;
      }
    }

    summary(key) {
      const lines = [];
      const add = (label, value) => {
        if (value === undefined || value === null) return;
        const text = Array.isArray(value) ? value.join(', ') : String(value).trim();
        if (text) lines.push(label ? `${label} : ${text}` : text);
      };
      switch (key) {
        case 'occasion': add('', this.labelOf('sp_occasion')); break;
        case 'projet': add('', this.val('sp_projet')); break;
        case 'supports': add('', this.supports()); break;
        case 'quantites':
          this.qtyRows().forEach((name) => {
            const label = name === 'Quantité totale' ? 'Quantité totale estimée' : name;
            add(label, this.data[`sp_qty_${slug(name)}`]);
          });
          if (this.qtyRows().length > 1) add('Total estimé', this.totalQty());
          break;
        case 'groupe':
          add('Noms ou numéros différents', this.val('sp_noms'));
          add('Répartition des tailles connue', this.val('sp_tailles'));
          add('Détail des tailles', this.val('sp_tailles_detail'));
          break;
        case 'couleurs':
          if (this.supports().includes('T-shirt')) add('T-shirts', this.val('sp_couleurs_tshirt'));
          if (!this.root.querySelector('[data-sp-other-colors]').hidden || !this.supports().includes('T-shirt')) {
            add(this.supports().includes('T-shirt') ? 'Autres supports' : '', this.val('sp_couleurs_autres'));
          }
          break;
        case 'visuel': add('', this.val('sp_visuel')); break;
        case 'perso':
          add('Texte', this.val('sp_texte'));
          add('Style', this.val('sp_style'));
          add('Couleurs du motif', this.val('sp_couleurs_motif'));
          add('Emplacements', this.val('sp_emplacements'));
          break;
        case 'references': add('', this.val('sp_references')); break;
        case 'date':
          if (this.val('sp_date')) add('', formatDate(this.val('sp_date')));
          else if (this.val('sp_date_flexible').length) add('', 'Pas de date précise');
          break;
        case 'livraison':
          add('Pays', this.val('sp_pays'));
          add('Code postal', this.val('sp_cp'));
          add('Envoi', this.val('sp_envoi'));
          add('Besoin particulier', this.val('sp_besoin'));
          break;
        case 'budget': add('', this.val('sp_budget')); break;
        case 'contact':
          add('Prénom', this.val('contact[name]'));
          add('E-mail', this.val('contact[email]'));
          add('Téléphone', this.val('contact[phone]'));
          break;
        default: break;
      }
      return lines.join('\n');
    }

    renderRecap() {
      this.renderColors();
      this.recapEl.innerHTML = '';
      this.visibleSteps().filter((key) => key !== 'recap').forEach((key) => {
        const label = this.steps[key].dataset.recap || key;
        const row = document.createElement('div');
        row.className = 'sp-recap__row';
        const dt = document.createElement('dt');
        dt.textContent = label;
        const dd = document.createElement('dd');
        dd.textContent = this.summary(key) || 'Non précisé';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.goto = key;
        btn.textContent = 'Modifier';
        btn.setAttribute('aria-label', `Modifier : ${label}`);
        row.append(dt, btn, dd);
        this.recapEl.append(row);
      });
    }

    /* ---------- Validation ---------- */
    check(key) {
      const errors = [];
      const err = (message, name) => errors.push({ message, name });
      switch (key) {
        case 'occasion':
          if (!this.val('sp_occasion')) err('Choisissez une occasion pour continuer.', 'sp_occasion');
          break;
        case 'projet':
          if (this.val('sp_projet').length < 15) err('Décrivez votre projet en quelques mots (15 caractères minimum).', 'sp_projet');
          break;
        case 'supports':
          if (!this.supports().length) err('Sélectionnez au moins un support, ou « À conseiller ».', 'sp_supports');
          break;
        case 'quantites': {
          const rows = this.qtyRows();
          if (!rows.length) err('Choisissez d\'abord un support.', 'sp_supports');
          rows.forEach((name) => {
            const key2 = `sp_qty_${slug(name)}`;
            const raw = String(this.data[key2] || '').trim();
            const n = Number(raw);
            if (!raw || !Number.isInteger(n) || n < 1 || n > 10000) {
              const label = name === 'Quantité totale' ? 'la quantité totale' : name.toLowerCase();
              err(`Indiquez un nombre entier d'au moins 1 pour ${label}.`, key2);
            }
          });
          break;
        }
        case 'groupe':
          if (!this.val('sp_noms')) err('Indiquez si des noms ou numéros différents sont nécessaires (« Je ne sais pas encore » est possible).', 'sp_noms');
          if (!this.val('sp_tailles')) err('Indiquez si la répartition des tailles est connue (« Pas encore » est possible).', 'sp_tailles');
          break;
        case 'couleurs':
          if (this.supports().includes('T-shirt') && !this.val('sp_couleurs_tshirt').length) {
            err('Choisissez au moins une couleur de t-shirt, ou « Pas encore décidé ».', 'sp_couleurs_tshirt');
          }
          break;
        case 'visuel':
          if (!this.val('sp_visuel')) err('Indiquez où en est votre visuel.', 'sp_visuel');
          break;
        case 'references': {
          const bad = this.val('sp_references').split(/\n+/).map((l) => l.trim()).filter(Boolean).filter((l) => !isUrlLine(l));
          if (bad.length) err(`Ce lien ne semble pas valide : « ${bad[0].slice(0, 60)} ». Chaque ligne doit commencer par https://, http:// ou www.`, 'sp_references');
          break;
        }
        case 'date': {
          const date = this.val('sp_date');
          const flexible = this.val('sp_date_flexible').length > 0;
          if (!date && !flexible) err('Indiquez une date, ou cochez « Pas de date précise ».', 'sp_date');
          else if (date && date < todayIso()) err('La date souhaitée ne peut pas être déjà passée.', 'sp_date');
          break;
        }
        case 'livraison': {
          if (!this.val('sp_pays')) err('Choisissez un pays de livraison.', 'sp_pays');
          const cp = this.val('sp_cp');
          if (this.val('sp_pays') === 'France') {
            if (!/^\d{5}$/.test(cp)) err('Indiquez un code postal français à 5 chiffres.', 'sp_cp');
          } else if (cp.length < 2) {
            err('Indiquez le code postal de livraison.', 'sp_cp');
          }
          if (!this.val('sp_envoi')) err('Choisissez une préférence d\'envoi (« À définir » est possible).', 'sp_envoi');
          break;
        }
        case 'contact': {
          if (!this.val('contact[name]')) err('Indiquez votre prénom.', 'contact[name]');
          const email = this.val('contact[email]');
          if (!email) err('Indiquez votre adresse e-mail.', 'contact[email]');
          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) err('Cette adresse e-mail ne semble pas valide.', 'contact[email]');
          const phone = this.val('contact[phone]');
          if (phone && !/^\+?[0-9 ().-]{6,20}$/.test(phone)) err('Ce numéro de téléphone ne semble pas valide.', 'contact[phone]');
          break;
        }
        default: break;
      }
      return errors;
    }

    validate(key) {
      this.steps[key] && this.steps[key].querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
      const errors = this.check(key);
      if (!errors.length) {
        this.hideError();
        return true;
      }
      this.showError(errors.map((e) => e.message).join(' '));
      let first = null;
      errors.forEach((e) => {
        this.fields(e.name).forEach((el) => {
          if (el.type !== 'radio' && el.type !== 'checkbox') el.setAttribute('aria-invalid', 'true');
          if (!first) first = el;
        });
      });
      if (first) {
        const describedId = this.errorEl.id || (this.errorEl.id = `${this.form.id}-error`);
        first.setAttribute('aria-describedby', describedId);
        first.focus({ preventScroll: false });
      }
      return false;
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      const step = this.steps[this.current];
      if (step && this.errorEl.previousElementSibling !== step) step.after(this.errorEl);
    }

    hideError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    /* ---------- Envoi ---------- */
    compile() {
      const occasion = this.labelOf('sp_occasion') || 'Projet';
      const name = this.val('contact[name]');
      const subject = `Demande de devis — ${occasion} — ${name}`;
      const blocks = [
        'DEMANDE DE DEVIS — SOLANA',
        `Envoyée le ${new Date().toLocaleString('fr-FR')} depuis ${window.location.origin}${window.location.pathname}`,
        ''
      ];
      this.visibleSteps().filter((key) => key !== 'recap').forEach((key) => {
        const label = (this.steps[key].dataset.recap || key).toUpperCase();
        blocks.push(label);
        blocks.push(this.summary(key) || 'Non précisé');
        blocks.push('');
      });
      this.root.querySelector('[data-sp-out-subject]').value = subject;
      this.root.querySelector('[data-sp-out-body]').value = blocks.join('\n').trim();
    }

    onSubmit(event) {
      if (this.current !== 'recap') {
        event.preventDefault();
        this.next();
        return;
      }
      const invalid = this.firstInvalid();
      if (invalid) {
        event.preventDefault();
        this.goto(invalid);
        this.validate(invalid);
        return;
      }
      const trap = this.root.querySelector('[data-sp-trap]');
      if (trap && trap.value) {
        event.preventDefault();
        return;
      }
      this.compile();
      this.collect();
      store.set(SENT_KEY, String(Date.now()));
      this.submitBtn.disabled = true;
      this.submitBtn.textContent = 'Envoi en cours…';
      /* Envoi natif : Shopify recharge la page avec le résultat. */
    }

    handleServerState() {
      const sentAt = parseInt(store.get(SENT_KEY), 10) || 0;
      const recent = sentAt && Date.now() - sentAt < 30 * 60 * 1000;
      const posted = this.root.querySelector('[data-sp-posted]');
      const serverErrors = this.root.querySelector('[data-sp-server-errors]');

      if (posted && recent) {
        store.remove(SENT_KEY);
        store.remove(STORE_KEY);
        this.form.reset();
        this.data = {};
        this.current = 'occasion';
        this.cleanUrl();
        this.open();
        this.goto('done');
        store.remove(STORE_KEY);
        return;
      }

      if (serverErrors && recent) {
        store.remove(SENT_KEY);
        this.cleanUrl();
        this.current = 'recap';
        this.open();
        const message = serverErrors.textContent.replace(/\s+/g, ' ').trim();
        this.showError(`L'envoi n'a pas abouti${message ? ` : ${message}` : ''}. Vérifiez vos réponses puis réessayez.`);
        return;
      }

      if (sentAt && !recent) store.remove(SENT_KEY);
    }

    cleanUrl() {
      if (/[?&]contact_posted=/.test(window.location.search) || window.location.hash) {
        const params = new URLSearchParams(window.location.search);
        params.delete('contact_posted');
        const query = params.toString();
        history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : ''));
      }
    }
  }

  /* ---------- Initialisation et liens #devis ---------- */
  const instances = [];
  const init = () => {
    document.querySelectorAll('[data-sp-devis]').forEach((root) => {
      if (!root.spDevis) {
        root.spDevis = new Devis(root);
        instances.push(root.spDevis);
      }
    });
  };

  const target = () => instances.find((i) => !i.dialog) || instances[0];

  const parseHash = (hash) => {
    const match = /^#devis(?:-([a-z]+))?$/.exec(hash || '');
    return match ? { occasion: match[1] || '' } : null;
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
    let url;
    try { url = new URL(link.getAttribute('href'), window.location.href); } catch (e) { return; }
    const parsed = parseHash(url.hash);
    if (!parsed || url.origin !== window.location.origin) return;
    const instance = target();
    if (!instance) return;
    event.preventDefault();
    instance.open(parsed.occasion);
  });

  const start = () => {
    init();
    const parsed = parseHash(window.location.hash);
    if (parsed && target() && target().current !== 'done' && !(target().dialog && target().dialog.open)) {
      target().open(parsed.occasion);
    }
  };

  window.addEventListener('hashchange', () => {
    const parsed = parseHash(window.location.hash);
    const instance = target();
    if (parsed && instance && !(instance.dialog && instance.dialog.open)) instance.open(parsed.occasion);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('shopify:section:load', init);
})();
